import { useEffect, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import type { SmsTemplate } from 'app/generated/graphql'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { SmsTemplateInput } from 'app/stores/SmsTemplateStore'
import { useSmsTemplateStore } from 'app/stores/StoreContext'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

export type UseSmsTemplatesReturn = {
  templates: SmsTemplate[]
  saving: boolean
  search: string
  setSearch: (value: string) => void
  editing: SmsTemplate | `new` | undefined
  openNew: () => void
  openEdit: (template: SmsTemplate) => void
  closeEditor: () => void
  saveTemplate: (input: SmsTemplateInput) => Promise<void>
  removeTemplate: (template: SmsTemplate) => Promise<void>
}

export function useSmsTemplates(): UseSmsTemplatesReturn {
  const store = useSmsTemplateStore()
  const workspace = useWorkspace()
  const [confirm] = useConfirmation()
  const [search, setSearch] = useState(``)
  const [editing, setEditing] = useState<SmsTemplate | `new` | undefined>()

  useEffect(() => {
    bg(store.load())
  }, [workspace?.id])

  const q = search.trim().toLowerCase()
  const templates = store
    .forWorkspace(workspace?.id)
    .filter((t) => !q || t.name.toLowerCase().includes(q) || t.body.toLowerCase().includes(q))

  const saveTemplate = async (input: SmsTemplateInput) => {
    if (!editing) return
    const result = editing === `new` ? await store.create(input) : await store.update(editing.id, input)
    if (result.error) {
      notifyError(`Couldn't save the template`)
      logError(`useSmsTemplates: save failed`, result.error.message)
      return
    }
    notifySuccess(editing === `new` ? `Template created` : `Template updated`)
    setEditing(undefined)
  }

  const removeTemplate = async (template: SmsTemplate) => {
    const confirmed = await confirm({
      title: `Delete template`,
      description: `Delete "${template.name}"? Messages already sent are not affected.`,
      variant: `danger`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    const result = await store.remove(template.id)
    if (result.error) {
      notifyError(`Couldn't delete the template`)
      logError(`useSmsTemplates: delete failed`, result.error.message)
      return
    }
    notifySuccess(`Template deleted`)
  }

  return {
    templates,
    saving: store.saving,
    search,
    setSearch,
    editing,
    openNew: () => setEditing(`new`),
    openEdit: setEditing,
    closeEditor: () => setEditing(undefined),
    saveTemplate,
    removeTemplate,
  }
}
